import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ExternalLink, MapPin } from 'lucide-react';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component to show Explore Opportunities link if a default landing page exists
function ExploreOpportunitiesLink() {
  const [defaultSlug, setDefaultSlug] = useState(null);
  
  useEffect(() => {
    const fetchDefault = async () => {
      try {
        const response = await fetch(`${API_URL}/api/public/landing-pages/default`);
        if (response.ok) {
          const data = await response.json();
          if (data.slug) {
            setDefaultSlug(data.slug);
          }
        }
      } catch (error) {
        logger.error('Error fetching default landing page:', error);
      }
    };
    fetchDefault();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  if (!defaultSlug) return null;
  
  return (
    <div className="pt-2">
      <Link 
        to={`/lp/${defaultSlug}`}
        className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 hover:underline"
        data-testid="explore-opportunities-link"
      >
        Explore Opportunities <ExternalLink size={14} />
      </Link>
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Check if already authenticated
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        logger.error('Auth check error:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-slate-600 font-body">Checking authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo-blue.png" 
                alt="Goldsands" 
                className="h-20 w-20 object-contain"
              />
            </div>
            <h1 className="font-heading text-3xl text-slate-900 font-bold tracking-wide">
              GOLDSANDS
            </h1>
            <p className="mt-1 text-[#1e3a5f] text-xs font-semibold tracking-widest">
              GROUP INC
            </p>
          </div>

          {/* Login Card */}
          <Card className="card-goldsands border-0 shadow-xl">
            <CardContent className="p-6 sm:p-8">
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-heading font-semibold text-slate-900">
                    Welcome Back
                  </h2>
                  <p className="mt-2 text-slate-500 font-body text-sm">
                    Sign in to access your dashboard
                  </p>
                </div>

                <Button 
                  onClick={handleGoogleLogin}
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transition-all duration-200"
                  data-testid="google-login-btn"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Sign in with Google
                </Button>

                <p className="text-center text-xs text-slate-400 font-body">
                  By signing in, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Public Forms Link */}
          <div className="text-center space-y-3">
            <p className="text-sm text-slate-500">Quick Access</p>
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline"
                onClick={() => navigate('/field-entry')}
                className="text-gold border-gold hover:bg-gold/10"
                data-testid="field-entry-link"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Field Data Entry
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/event-register')}
                className="text-slate-600 border-slate-300 hover:bg-slate-50"
                data-testid="event-register-link"
              >
                Event Registration Form
              </Button>
            </div>
            
            <ExploreOpportunitiesLink />
          </div>

          {/* Features */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gold/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-xs font-body text-slate-600">Lead Management</p>
            </div>
            <div className="p-4">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gold/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-xs font-body text-slate-600">Sales Tracking</p>
            </div>
            <div className="p-4">
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gold/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-xs font-body text-slate-600">Collections</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Image */}
      <div 
        className="hidden lg:flex flex-1 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758448500872-0d31e905a9e7?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      >
        {/* Gold Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-gold/30"></div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="max-w-md">
            <h2 className="font-heading text-3xl text-white font-semibold mb-4">
              Real Estate Investment Excellence
            </h2>
            <p className="text-slate-300 font-body">
              Connecting buyers in the USA with premium land plots, apartments, and hotel shares in Bangladesh.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-1 w-12 bg-gold rounded"></div>
              <span className="text-gold font-body text-sm">Goldsands Group INC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
